<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { getGeneratedRecordResponsePdf } from '@/entities/GeneratedRecord';
  import { ColoredButton, SimpleLoading } from '@/shared/ui';

  const props = defineProps<{
    recordId: number;
    responseNumber: number;
    fileName?: string;
  }>();

  const LOAD_ERROR = 'Não foi possível carregar o PDF. Tente novamente.';

  const url = ref<string | null>(null);
  const loading = ref(true);
  const failed = ref(false);
  let latestRequest = 0;

  const frameTitle = computed(() =>
    props.fileName
      ? `PDF da resposta ${props.responseNumber}: ${props.fileName}`
      : `PDF da resposta ${props.responseNumber}`,
  );

  function revoke(): void {
    if (url.value) URL.revokeObjectURL(url.value);
    url.value = null;
  }

  async function load(): Promise<void> {
    // Only the newest request may make a URL: one made for a response already left would never be
    // revoked.
    const request = ++latestRequest;
    revoke();
    loading.value = true;
    failed.value = false;

    try {
      const blob = await getGeneratedRecordResponsePdf(props.recordId, props.responseNumber);
      if (request !== latestRequest) return;
      url.value = URL.createObjectURL(blob);
    } catch {
      if (request === latestRequest) failed.value = true;
    } finally {
      if (request === latestRequest) loading.value = false;
    }
  }

  watch(() => [props.recordId, props.responseNumber], load, { immediate: true });

  onBeforeUnmount(() => {
    latestRequest += 1;
    revoke();
  });
</script>

<template>
  <div
    class="flex h-full min-h-[480px] flex-col"
    data-testid="fill-logs-pdf-view"
  >
    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center"
      data-testid="fill-logs-pdf-loading"
    >
      <SimpleLoading />
    </div>
    <div
      v-else-if="failed"
      class="flex flex-1 flex-col items-center gap-4 py-8 text-center"
      data-testid="fill-logs-pdf-error"
    >
      <p class="text-lg text-gray-07">
        {{ LOAD_ERROR }}
      </p>
      <ColoredButton
        text="Tentar novamente"
        data-testid="fill-logs-pdf-retry"
        @click="load"
      />
    </div>
    <iframe
      v-else-if="url"
      :src="url"
      :title="frameTitle"
      class="w-full flex-1 rounded-md border border-gray-02 bg-white-01"
      data-testid="fill-logs-pdf-frame"
    />
  </div>
</template>
